import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  streamingServiceApi,
  type PricePlan,
  type StreamingService,
} from "@/services/streaming-service";
import { Loader2, HelpCircle } from "lucide-react";

interface PricePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: StreamingService;
  editPlan?: PricePlan | null;
}

interface PricePlanFormData {
  name: string;
  price: number;
  status: "ACTIVE" | "INACTIVE";
  isDefault: boolean;
  pricePlanType: "SLOT" | "FULL_ACCOUNT";
  duration: number;
  updateSub: boolean;
}

const DURATION_OPTIONS = [
  { value: 7, label: "7 dias", defaultName: "Plan Semanal" },
  { value: 15, label: "15 dias", defaultName: "Plan Quincenal" },
  { value: 30, label: "30 dias (1 mes)", defaultName: "Plan Mensual" },
  { value: 60, label: "60 dias (2 meses)", defaultName: "Plan Bimestral" },
  { value: 90, label: "90 dias (3 meses)", defaultName: "Plan Trimestral" },
  { value: 180, label: "180 dias (6 meses)", defaultName: "Plan Semestral" },
  { value: 365, label: "365 dias (1 ano)", defaultName: "Plan Anual" },
];

// Obtener nombre predefinido según la duración
function getDefaultNameByDuration(duration: number): string {
  const option = DURATION_OPTIONS.find((opt) => opt.value === duration);
  return option?.defaultName || `Plan ${duration} días`;
}

export function PricePlanModal({
  open,
  onOpenChange,
  service,
  editPlan,
}: PricePlanModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!editPlan;

  const [formData, setFormData] = useState<PricePlanFormData>({
    name: "",
    price: 0,
    status: "ACTIVE",
    isDefault: false,
    pricePlanType: "SLOT",
    duration: 30,
    updateSub: false,
  });

  // Cargar datos del plan si estamos editando
  useEffect(() => {
    if (editPlan) {
      setFormData({
        name: editPlan.name || "",
        price: Number(editPlan.price) || 0,
        status: editPlan.status,
        isDefault: editPlan.isDefault,
        pricePlanType: editPlan.pricePlanType.includes("FULL_ACCOUNT") ? "FULL_ACCOUNT" : "SLOT",
        duration: editPlan.duration || 30,
        updateSub: false,
      });
    } else {
      const defaultDuration = 30;
      setFormData({
        name: getDefaultNameByDuration(defaultDuration),
        price: 0,
        status: "ACTIVE",
        isDefault: false,
        pricePlanType: "SLOT",
        duration: defaultDuration,
        updateSub: false,
      });
    }
  }, [editPlan, open]);

  // Actualizar nombre cuando cambia la duración (solo si no estamos editando)
  const handleDurationChange = (value: string) => {
    const newDuration = parseInt(value);
    const currentDefaultName = getDefaultNameByDuration(formData.duration);

    // Solo actualizar el nombre si es el nombre predefinido actual o está vacío
    const shouldUpdateName = !isEditing && (
      formData.name === currentDefaultName ||
      formData.name === "" ||
      DURATION_OPTIONS.some(opt => opt.defaultName === formData.name)
    );

    setFormData((prev) => ({
      ...prev,
      duration: newDuration,
      name: shouldUpdateName ? getDefaultNameByDuration(newDuration) : prev.name,
    }));
  };

  // Mutation para registrar o actualizar plan de precio
  const updateMutation = useMutation({
    mutationFn: async (newPlan: PricePlanFormData) => {
      const planData = {
        name: newPlan.name,
        price: newPlan.price,
        duration: newPlan.duration,
        status: newPlan.status,
        isDefault: newPlan.isDefault,
        pricePlanType: newPlan.pricePlanType,
      };

      if (isEditing && editPlan) {
        return streamingServiceApi.updatePricePlan(editPlan.id, {
          ...planData,
          updateSub: newPlan.updateSub,
        });
      }

      return streamingServiceApi.registerPricePlan(Number(service.id), planData);
    },
    onSuccess: () => {
      toast.success(
        isEditing
          ? "Plan de precio actualizado exitosamente"
          : "Plan de precio agregado exitosamente"
      );
      queryClient.invalidateQueries({ queryKey: ["streaming-service", service.id] });
      queryClient.invalidateQueries({ queryKey: ["streaming-services"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    const defaultDuration = 30;
    setFormData({
      name: getDefaultNameByDuration(defaultDuration),
      price: 0,
      status: "ACTIVE",
      isDefault: false,
      pricePlanType: "SLOT",
      duration: defaultDuration,
      updateSub: false,
    });
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    if (!Number.isFinite(formData.price) || formData.price <= 0) {
      toast.error("El precio debe ser mayor a 0");
      return;
    }

    updateMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Plan de Precio" : "Agregar Plan de Precio"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifica los detalles del plan de precio."
              : `Agrega un nuevo plan de precio para ${service.name}.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              placeholder="Ej: Plan Mensual, Plan Trimestral..."
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>

          {/* Duracion */}
          <div className="space-y-2">
            <Label htmlFor="duration">Duracion *</Label>
            <Select
              value={formData.duration.toString()}
              onValueChange={handleDurationChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona la duracion" />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Precio */}
          <div className="space-y-2">
            <Label htmlFor="price">Precio *</Label>
            <Input
              id="price"
              type="number"
              min={0}
              step={0.01}
              placeholder="0.00"
              value={formData.price ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  price: value === "" ? undefined : parseFloat(value),
                }));
              }}
              onBlur={() => {
                if (!Number.isFinite(formData.price)) {
                  setFormData((prev) => ({ ...prev, price: 0 }));
                }
              }}
            />
          </div>

          {/* Tipo de plan */}
          <div className="space-y-2">
            <Label htmlFor="pricePlanType">Tipo de plan</Label>
            <Select
              value={formData.pricePlanType}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  pricePlanType: value as "SLOT" | "FULL_ACCOUNT",
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SLOT">Por Slot/Perfil</SelectItem>
                <SelectItem value="FULL_ACCOUNT">Cuenta Completa</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Estado */}
          <div className="space-y-2">
            <Label htmlFor="status">Estado</Label>
            <Select
              value={formData.status}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  status: value as "ACTIVE" | "INACTIVE",
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Activo</SelectItem>
                <SelectItem value="INACTIVE">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Es default */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isDefault">Plan por defecto</Label>
              <p className="text-sm text-muted-foreground">
                Este plan se seleccionara automaticamente
              </p>
            </div>
            <Switch
              id="isDefault"
              checked={formData.isDefault}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isDefault: checked }))
              }
            />
          </div>

          {/* Actualizar suscripciones existentes (solo al editar) */}
          {isEditing && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="updateSub">Actualizar suscripciones existentes</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[260px]">
                      Al activarlo, las suscripciones que ya fueron creadas con
                      este plan también se actualizarán con el nuevo precio.
                      Si lo dejas desactivado, solo los nuevos registros usarán
                      el precio actualizado.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-sm text-muted-foreground">
                  Aplica el nuevo precio a las suscripciones ya creadas con este plan
                </p>
              </div>
              <Switch
                id="updateSub"
                checked={formData.updateSub}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, updateSub: checked }))
                }
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEditing ? "Guardar" : "Agregar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
